import { ChangeDetectionStrategy, Component, computed, inject, Injector, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { EntityManifestService, type EntitySurfaceOperation } from '@workbuddy/services/entities';
import { getEntityBySlug } from '@workbuddy/services/entities/entity.manifest.registry';
import { EntityInspectorRendererComponent, type EntityInspectorRenderTarget } from '@workbuddy/ui/v2/blocks/entity.inspector';

/**
 * Generic full-page host for one entity record: `/entity/:slug/:operation`.
 *
 * One route serves every entity, the same way `entityInspectorRoute` does — the slug is the
 * discriminator and the manifest supplies the surface, so opting an entity in is a manifest change
 * rather than a new route. This is the durable, shareable URL an inspector's *Open full page* hands
 * off to; the inspector itself stays transient and its outlet URL never needs to be pasteable.
 *
 * The renderer is the inspector's, unchanged. It takes a plain target and resolves the surface from
 * the manifest, so the only difference here is the absence of panel chrome.
 */
@Component({
  selector: 'entity-record-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [EntityInspectorRendererComponent],
  template: `
    @if ($target(); as target) {
      <entity-inspector-renderer [target]="target" />
    } @else {
      <div class="entity-record-page-unknown">
        <h2>Not found</h2>
        <p>This record cannot be opened here.</p>
      </div>
    }
  `,
  styles: [`
    /* .shell-content > * is already position:absolute inset:0, so this must NOT set its own height —
       a percentage height would resolve against that and collapse. Flex column instead, matching how
       entity-board fills the same slot, with the margin and radius from .entity-board-top-level so a
       record page and a top-level board sit identically in the working area. */
    entity-record-page {
      display: flex;
      flex-direction: column;
      margin: 12px;
      margin-left: 8px;
      width: calc(100% - 20px);
      height: calc(100% - 24px);
      overflow: hidden;
      border-radius: 8px;
      background: var(--wb-color-background-primary, #fff);
    }

    entity-record-page > entity-inspector-renderer {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }

    .entity-record-page-unknown {
      padding: 24px;
    }
  `],
})
export class EntityRecordPageComponent {

  private readonly route = inject(ActivatedRoute);
  private readonly manifests = inject(EntityManifestService);
  private readonly injector = inject(Injector);

  private readonly $params = toSignal(this.route.paramMap, { initialValue: this.route.snapshot.paramMap });

  /**
   * Null renders not-found rather than throwing. A URL here is untrusted input — pasted, bookmarked,
   * or left over from an entity that has since dropped its slug — so an unresolvable one is an
   * ordinary outcome, not an error.
   */
  protected readonly $target = computed<EntityInspectorRenderTarget | null>(() => {

    const params = this.$params();
    const slug = params.get('slug');
    if (!slug) return null;

    const entity = getEntityBySlug(slug);
    if (!entity) return null;

    const operation = params.get('operation') as EntitySurfaceOperation | null;
    if (!operation || !this.manifests.supportsOperation(entity, operation)) return null;

    const id = params.get('id') ?? undefined;
    // Only create may omit an id; every other operation needs a record to act on.
    if (!id && operation !== 'create') return null;

    // Authorization is NOT rechecked here. `entityOperationRouteGuard` ran it before this route
    // committed, with the record loaded so record-state rules could be answered — neither of which a
    // computed can do, since `authorizeOperation` is async. The checks above are kept because they are
    // cheap and make the component correct on its own terms if it is ever mounted outside that route.
    return { entity, id, operation };
  });
}
